import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Lazy } from 'vscode/vscode/vs/base/common/lazy';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { findInFilesCommand } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchActionsFind';
import { isDetachedTerminalInstance } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal';
import { ITerminalService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { registerActiveXtermAction, registerActiveInstanceAction } from '../../../terminal/browser/terminalActions.js';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { TerminalFindWidget } from './terminalFindWidget.js';

var TerminalFindContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/find/browser/terminal.find.contribution";
let TerminalFindContribution = class TerminalFindContribution extends Disposable {
    static { TerminalFindContribution_1 = this; }
    static { this.ID = 'terminal.find'; }
    static get(instance) {
        return instance.getContribution(TerminalFindContribution_1.ID);
    }
    get findWidget() { return this._findWidget.value; }
    constructor(_instance, processManager, widgetManager, instantiationService, terminalService) {
        super();
        this._instance = _instance;
        this._findWidget = ( (new Lazy(() => {
            const findWidget = instantiationService.createInstance(TerminalFindWidget, this._instance);
            findWidget.focusTracker.onDidFocus(() => {
                TerminalFindContribution_1.activeFindWidget = this;
                this._instance.forceScrollbarVisibility();
                if (!isDetachedTerminalInstance(this._instance)) {
                    terminalService.setActiveInstance(this._instance);
                }
            });
            findWidget.focusTracker.onDidBlur(() => {
                TerminalFindContribution_1.activeFindWidget = undefined;
                this._instance.resetScrollbarVisibility();
            });
            if (!this._instance.domElement) {
                throw ( (new Error('FindWidget expected terminal DOM to be initialized')));
            }
            this._instance.domElement?.appendChild(findWidget.getDomNode());
            if (this._lastLayoutDimensions) {
                findWidget.layout(this._lastLayoutDimensions.width);
            }
            return findWidget;
        })));
    }
    layout(_xterm, dimension) {
        this._lastLayoutDimensions = dimension;
        this._findWidget.rawValue?.layout(dimension.width);
    }
    xtermReady(xterm) {
        this._register(xterm.onDidChangeFindResults(() => this._findWidget.rawValue?.updateResultCount()));
    }
    dispose() {
        if (TerminalFindContribution_1.activeFindWidget === this) {
            TerminalFindContribution_1.activeFindWidget = undefined;
        }
        super.dispose();
        this._findWidget.rawValue?.dispose();
    }
};
TerminalFindContribution = TerminalFindContribution_1 = ( (__decorate([
    ( (__param(3, IInstantiationService))),
    ( (__param(4, ITerminalService)))
], TerminalFindContribution)));
registerTerminalContribution(TerminalFindContribution.ID, TerminalFindContribution, true);
registerActiveXtermAction({
    id: "workbench.action.terminal.focusFind" ,
    title: ( localize2WithPath(_moduleId, 0, 'Focus Find')),
    keybinding: {
        primary: 2048  | 36 ,
        when: ( (ContextKeyExpr.or(TerminalContextKeys.findFocus, TerminalContextKeys.focusInAny))),
        weight: 200
    },
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        contr?.findWidget.reveal();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.hideFind" ,
    title: ( localize2WithPath(_moduleId, 1, 'Hide Find')),
    keybinding: {
        primary: 9 ,
        secondary: [1024  | 9 ],
        when: ( (ContextKeyExpr.and(TerminalContextKeys.focusInAny, TerminalContextKeys.findVisible))),
        weight: 200
    },
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        contr?.findWidget.hide();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.toggleFindRegex" ,
    title: ( localize2WithPath(_moduleId, 2, 'Toggle Find Using Regex')),
    keybinding: {
        primary: 512  | 48 ,
        mac: { primary: 2048  | 512  | 48  },
        when: TerminalContextKeys.findVisible,
        weight: 200
    },
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        const state = contr?.findWidget.state;
        state?.change({ isRegex: !state.isRegex }, false);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.toggleFindWholeWord" ,
    title: ( localize2WithPath(_moduleId, 3, 'Toggle Find Using Whole Word')),
    keybinding: {
        primary: 512  | 53 ,
        mac: { primary: 2048  | 512  | 53  },
        when: TerminalContextKeys.findVisible,
        weight: 200
    },
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        const state = contr?.findWidget.state;
        state?.change({ wholeWord: !state.wholeWord }, false);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.toggleFindCaseSensitive" ,
    title: ( localize2WithPath(_moduleId, 4, 'Toggle Find Using Case Sensitive')),
    keybinding: {
        primary: 512  | 33 ,
        mac: { primary: 2048  | 512  | 33  },
        when: TerminalContextKeys.findVisible,
        weight: 200
    },
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        const state = contr?.findWidget.state;
        state?.change({ matchCase: !state.matchCase }, false);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.findNext" ,
    title: ( localize2WithPath(_moduleId, 5, 'Find Next')),
    keybinding: [
        {
            primary: 61 ,
            mac: { primary: 2048  | 37 , secondary: [61 ] },
            when: ( (ContextKeyExpr.or(TerminalContextKeys.focusInAny, TerminalContextKeys.findFocus))),
            weight: 200
        },
        {
            primary: 1024  | 3 ,
            when: TerminalContextKeys.findInputFocus,
            weight: 200
        }
    ],
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        const widget = contr?.findWidget;
        if (widget) {
            widget.show();
            widget.find(false);
        }
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.findPrevious" ,
    title: ( localize2WithPath(_moduleId, 6, 'Find Previous')),
    keybinding: [
        {
            primary: 1024  | 61 ,
            mac: { primary: 2048  | 1024  | 37 , secondary: [1024  | 61 ] },
            when: ( (ContextKeyExpr.or(TerminalContextKeys.focusInAny, TerminalContextKeys.findFocus))),
            weight: 200
        },
        {
            primary: 3 ,
            when: TerminalContextKeys.findInputFocus,
            weight: 200
        }
    ],
    precondition: ( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        const contr = TerminalFindContribution.activeFindWidget || TerminalFindContribution.get(activeInstance);
        const widget = contr?.findWidget;
        if (widget) {
            widget.show();
            widget.find(true);
        }
    }
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.searchWorkspace" ,
    title: ( localize2WithPath(_moduleId, 7, 'Search Workspace')),
    keybinding: [
        {
            primary: 2048  | 1024  | 36 ,
            when: ( (ContextKeyExpr.and(
                TerminalContextKeys.processSupported,
                TerminalContextKeys.focus,
                TerminalContextKeys.textSelected
            ))),
            weight: 200  + 50
        }
    ],
    run: (activeInstance, c, accessor) => findInFilesCommand(accessor, { query: activeInstance.selection })
});
